import { useState, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ImageUploadModal.css';

function ImageUploadModal({
	isOpen,
	onClose,
	onSave,
	currentImage,
	shouldCrop = false,
}) {
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [crop, setCrop] = useState();
	const imageRef = useRef(null);

	// Target size for the output image
	const TARGET_SIZE = 200;

	// Reset state when modal opens
	useEffect(() => {
		if (isOpen) {
			setSelectedFile(null);
			setPreviewUrl(null);
			setCrop(undefined);
		}
	}, [isOpen]);

	// Clean up any object URLs when component unmounts or modal closes
	useEffect(() => {
		return () => {
			if (previewUrl && previewUrl.startsWith('blob:')) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
		return centerCrop(
			makeAspectCrop(
				{
					unit: '%',
					width: 90,
				},
				aspect,
				mediaWidth,
				mediaHeight
			),
			mediaWidth,
			mediaHeight
		);
	}

	const onImageLoad = (e) => {
		const { width, height } = e.currentTarget;
		setCrop(centerAspectCrop(width, height, 1));
	};

	const handleFileSelect = (e) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];
			setSelectedFile(file);
			setPreviewUrl(URL.createObjectURL(file));
		}
	};

	const getCroppedImg = async (image, crop) => {
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');

		// Set canvas size to our desired output size
		canvas.width = TARGET_SIZE;
		canvas.height = TARGET_SIZE;

		// Calculate scaling factors
		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;

		// Calculate actual pixel values from percentages
		const cropX = (crop.x / 100) * image.width * scaleX;
		const cropY = (crop.y / 100) * image.height * scaleY;
		const cropWidth = (crop.width / 100) * image.width * scaleX;
		const cropHeight = (crop.height / 100) * image.height * scaleY;

		// Draw the image first
		ctx.save();
		ctx.beginPath();
		ctx.arc(
			TARGET_SIZE / 2,
			TARGET_SIZE / 2,
			TARGET_SIZE / 2,
			0,
			Math.PI * 2
		);
		ctx.clip();

		// Clear the canvas with a transparent background
		ctx.clearRect(0, 0, TARGET_SIZE, TARGET_SIZE);

		// Enable better image smoothing
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';

		// Draw the cropped portion of the image
		ctx.drawImage(
			image,
			cropX,
			cropY,
			cropWidth,
			cropHeight,
			0,
			0,
			TARGET_SIZE,
			TARGET_SIZE
		);

		ctx.restore();

		return new Promise((resolve) => {
			canvas.toBlob(
				(blob) => {
					if (!blob) {
						console.error('Canvas is empty');
						return;
					}
					blob.name = selectedFile.name;
					resolve(blob);
				},
				'image/png',
				1.0
			);
		});
	};

	const handleSave = async () => {
		if (!imageRef.current) return;

		try {
			if (shouldCrop) {
				// Original cropping logic for profile images
				if (!crop) return;
				const croppedImageBlob = await getCroppedImg(
					imageRef.current,
					crop
				);
				const croppedImageFile = new File(
					[croppedImageBlob],
					selectedFile.name.replace(/\.[^/.]+$/, '.png'),
					{
						type: 'image/png',
						lastModified: Date.now(),
					}
				);
				onSave({ file: croppedImageFile });
			} else {
				// For cover images, just convert to PNG without cropping
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				const img = imageRef.current;

				canvas.width = img.naturalWidth;
				canvas.height = img.naturalHeight;

				ctx.drawImage(img, 0, 0);

				const blob = await new Promise((resolve) => {
					canvas.toBlob(resolve, 'image/png', 1.0);
				});

				const coverImageFile = new File(
					[blob],
					selectedFile.name.replace(/\.[^/.]+$/, '.png'),
					{
						type: 'image/png',
						lastModified: Date.now(),
					}
				);

				onSave({ file: coverImageFile });
			}
		} catch (e) {
			console.error('Error processing image:', e);
		}
	};

	if (!isOpen) return null;

	return (
		<div className='modal-overlay'>
			<div className='modal-content'>
				<h2>Upload Image</h2>
				<p className='upload-hint'>
					{shouldCrop
						? `For best results, use a square image at least ${TARGET_SIZE}x${TARGET_SIZE} pixels.`
						: 'Select an image to upload.'}
				</p>
				<div className='image-preview-container'>
					{previewUrl ? (
						shouldCrop ? (
							<ReactCrop
								crop={crop}
								onChange={(_, percentCrop) =>
									setCrop(percentCrop)
								}
								aspect={1}
								circularCrop={true}
								minWidth={50}>
								<img
									ref={imageRef}
									src={previewUrl}
									onLoad={onImageLoad}
									style={{ maxWidth: '100%' }}
									alt='Preview'
								/>
							</ReactCrop>
						) : (
							<div className='preview-image-container'>
								<img
									ref={imageRef}
									src={previewUrl}
									alt='Preview'
								/>
							</div>
						)
					) : (
						<div className='image-placeholder'>
							<span className='material-icons'>image</span>
							<span>Select an image to upload</span>
						</div>
					)}
				</div>

				<div className='upload-controls'>
					<input
						type='file'
						accept='image/*'
						onChange={handleFileSelect}
					/>
				</div>

				<div className='modal-actions'>
					<button
						onClick={onClose}
						className='modal-button modal-button-secondary'>
						Cancel
					</button>
					<button
						onClick={handleSave}
						className='modal-button modal-button-primary'
						disabled={!selectedFile || (shouldCrop && !crop)}>
						Save
					</button>
				</div>
			</div>
		</div>
	);
}

export default ImageUploadModal;
